import React, {
  ForwardedRef,
  PropsWithChildren,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useRef,
  useState,
  MutableRefObject,
  CSSProperties,
} from 'react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import List from 'react-virtualized/dist/es/List';

import cls from 'classnames';

import noop from 'lodash/noop';
import _throttle from 'lodash/throttle';

import {
  SelectCustomRenderer,
  SelectOption,
  OptionGroupProps,
  SelectValue,
  CustomOptionProps,
  SelectProps,
} from '../../Select';
import {
  calculatePlacementAndStyles,
  PlacementStyles,
} from '../../helpers/calculatePlacementAndStyles';
import { PLACEMENT } from '../../helpers/constants';
import { ANIMATION_TIMEOUT } from '../../helpers/constants';
import { getActiveOrFirstOption } from '../../helpers/getActiveOrFirstOption';
import { getAnimationClassNames } from '../../helpers/getAnimationClassnames';
import { isOptionGroup } from '../../helpers/helpers';
import {
  AlignType,
  Placement,
  SelectMultipleValue,
  SelectSingleValue,
  Size,
} from '../../helpers/types';
import useKeyboardControl from '../../hooks/useKeyboardControl';
import usePrevious from '../../hooks/usePrevious';
import useWebApi from '../../hooks/useWebApi';
import Option from '../Option/Option';

import styles from './Overlay.scss';

export type ContentChildrenClassNames = {
  content?: string;
  optionWrapper?: string;
  contentWrapper?: string;
};

type VirtualizedOnScrollProps = {
  clientHeight: number;
  scrollHeight: number;
  scrollTop: number;
};

type OverlayProps<Option extends SelectOption = SelectOption> = PropsWithChildren<{
  CustomOptionRenderer?: SelectCustomRenderer<CustomOptionProps<Option>>;
  alignType: AlignType;
  childrenClassNames?: ContentChildrenClassNames;
  forwardedRef?: ForwardedRef<HTMLDivElement | null>;
  hasExternalControl?: boolean;
  isInFullscreenMode?: boolean;
  isMultiple?: boolean;
  isOpen: boolean;
  isVerticalAutoPlacementDisabled?: boolean;
  onSelect: (value: SelectValue, option: Option, shouldClose?: boolean) => void;
  options: (Option | OptionGroupProps)[];
  placement: Placement;
  shouldLimitHeightByScreen?: boolean;
  shouldSetInitialHover?: boolean;
  isVirtualized: boolean;
  virtualizationRowHeight: number;
  size: Size;
  specificLocator: Required<SelectProps['specificLocator']>;
  triggerRef: RefObject<HTMLElement>;
  value: SelectSingleValue | SelectMultipleValue;
  container?: null | HTMLElement;
}>;

const CONTENT_HEIGHT_PADDING = 16;

function getInitialOptionsRefs<Option extends SelectOption>(
  options: (Option | OptionGroupProps)[]
) {
  return options.reduce<{ [key: string]: null }>((acc, option) => {
    if (!isOptionGroup(option)) {
      acc[option.value] = null;
    }

    return acc;
  }, {});
}

function Overlay<Option extends SelectOption>(props: OverlayProps<Option>) {
  const {
    CustomOptionRenderer,
    alignType,
    childrenClassNames = {},
    forwardedRef,
    hasExternalControl = false,
    isInFullscreenMode = false,
    isMultiple = false,
    isOpen,
    isVerticalAutoPlacementDisabled = false,
    onSelect,
    options,
    placement,
    shouldLimitHeightByScreen = false,
    shouldSetInitialHover = true,
    isVirtualized,
    virtualizationRowHeight,
    size,
    specificLocator,
    triggerRef,
    value,
    container,
  } = props;
  // снепшот который мы рендерим при анимации закрытия (предыдущий рендер с не выбранными значениями)
  const [snapshot, setSnapshot] = useState<ReactElement | null>(null);
  // Нужно чтобы подскроливать в виртуализированном листе
  const [virtualizedScrollTop, setVirtualizedScrollTop] = useState(0);
  // тру когда placementStyles подсчитан, нужно чтобы скролить на initial ховер
  const [isPlacementCalculated, setIsPlacementCalculated] = useState(false);
  // Инфа о ширине контейнера, для использования в виртуализации
  const [contentWidth, setContentWidth] = useState(0);
  // Инфа о высоте контейнера, для использования в виртуализации в полноэкранном режиме
  const [contentHeight, setContentHeight] = useState(0);
  // стили которые используются для позиционирования оверлея
  const [placementStyles, setPlacementStyles] = useState<PlacementStyles>({} as PlacementStyles);
  // позиция оверлея сверху/снизу, конкретно тут отвечает за стиль флекса
  const [realPlacement, setRealPlacement] = useState(placement);
  // когда используем клавиатуру я отрубаю это, чтобы мышка которая стоит на варианте не триггерила ховер
  // когда юзер двигает мышкой я врубаю это обратно
  const [shouldUseMouseHover, setShouldUseMouseHover] = useState(true);
  // лочит переключение сверху/снизу во время того как оверлей открыт чтобы не дергалось
  const [shouldChangePlacementOnOverflowOnly, setShouldChangePlacementOnOverflowOnly] =
    useState(false);
  const [hoveredOption, setHoveredOption] = useState<Option | null>(null);
  // нужно чтобы показывать тень оверлея в фулскрине
  const [withShadow, setWithShadow] = useState(false);
  // эти рефы используются для перемещения стрелками и ховеров
  const optionsRefs = useRef<{ [key: string]: HTMLDivElement | null }>(
    getInitialOptionsRefs(options)
  );
  const contentRef = useRef<HTMLDivElement>(null);

  const hasTopPlacement = realPlacement === PLACEMENT.TOP;
  const isOverlayOpen = isOpen && options.length > 0;
  const isSmall = size === 'small';

  const handleContentRef = (node: HTMLDivElement | null) => {
    (contentRef as MutableRefObject<HTMLDivElement | null>).current = node;
    (forwardedRef as MutableRefObject<HTMLDivElement | null>).current = node;

    if (contentRef.current && isVirtualized) {
      const rect = contentRef.current.getBoundingClientRect();

      if (rect.width !== contentWidth || rect.height !== contentWidth) {
        setContentWidth(rect.width);
      }

      if (isInFullscreenMode && rect.height !== contentHeight) {
        setContentHeight(rect.height);
      }
    }
  };

  const renderOptionGroupLabel = (option: OptionGroupProps) => {
    return (
      <div key={option.label} className={cls(styles.optionGroupLabel, option.className)}>
        {option.label}
      </div>
    );
  };

  const renderOption = ({
    key,
    style,
    index,
  }: {
    key: React.Key;
    style: CSSProperties;
    index: number;
  }) => {
    const option = options[index];

    if (isOptionGroup(option)) {
      return renderOptionGroupLabel(option);
    }

    const isActive = Array.isArray(value) ? value.includes(option.value) : value === option.value;

    return (
      //оборачиваю во все хендлеры сразу тут чтобы не приходилось это делать в кастомном рендере
      <div
        style={style}
        key={key}
        className={cls(styles.disableOutline, childrenClassNames.optionWrapper, {
          [styles.overflowYHidden]: isVirtualized,
        })}
        ref={(node) => {
          optionsRefs.current[option.value] = node;
        }}
        onMouseLeave={() => shouldUseMouseHover && setHoveredOption(null)}
        onMouseEnter={() => !option.isDisabled && shouldUseMouseHover && setHoveredOption(option)}
        onMouseMove={() => {
          setShouldUseMouseHover(true);
          setHoveredOption(option);
        }}
        onClick={() => {
          if (!option.isDisabled) {
            onSelectOption(option.value, option);
          }
        }}
        tabIndex={-1}
      >
        {CustomOptionRenderer ? (
          <CustomOptionRenderer
            option={option}
            isActive={isActive}
            isHovered={option.value === hoveredOption?.value}
          />
        ) : (
          <Option
            option={option}
            size={size}
            isActive={isActive}
            isHovered={option.value === hoveredOption?.value}
            withCheckbox={Array.isArray(value)}
            specificLocator={specificLocator}
          />
        )}
      </div>
    );
  };

  const renderOptions = () => {
    if (isVirtualized) {
      const overallPadding = isInFullscreenMode ? 0 : CONTENT_HEIGHT_PADDING;
      let overallHeight = isInFullscreenMode
        ? contentHeight
        : placementStyles.maxHeight - overallPadding;

      if (options.length * virtualizationRowHeight < overallHeight) {
        overallHeight = options.length * virtualizationRowHeight;
      }

      return (
        <List
          width={contentWidth}
          height={overallHeight}
          rowCount={options.length}
          rowHeight={virtualizationRowHeight}
          rowRenderer={renderOption}
          onScroll={checkVirtualizedFullscreenContainerScroll}
          scrollTop={virtualizedScrollTop}
        />
      );
    }

    return options.map((option, index) =>
      renderOption({
        key: !isOptionGroup(option) ? option.value : option.label,
        style: {},
        index,
      })
    );
  };

  const checkVirtualizedFullscreenContainerScroll = useCallback(
    ({ scrollTop }: VirtualizedOnScrollProps) => {
      if (isInFullscreenMode) {
        if (scrollTop > 0) {
          setWithShadow(true);
        } else {
          setWithShadow(false);
        }
      }

      setVirtualizedScrollTop(scrollTop);

      return;
    },
    [isInFullscreenMode]
  );

  const checkFullscreenContainerScroll = useCallback((event: WheelEvent) => {
    const scrollTop = (event.target as HTMLElement).scrollTop;

    if (scrollTop > 0) {
      setWithShadow(true);
    } else {
      setWithShadow(false);
    }

    return;
  }, []);

  const applyPlacementStyles = () => {
    if (!contentRef.current || !triggerRef.current || isInFullscreenMode || !document) {
      return;
    }

    const defaultScrollContainer = !!document.body.scrollTop
      ? document.body
      : document.documentElement;

    const scrollContainer = container || defaultScrollContainer;

    const contentRect = contentRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();

    let scrollContainerRect: DOMRect | null = null;

    setContentWidth(0);
    setContentHeight(0);

    if (scrollContainer !== defaultScrollContainer) {
      scrollContainerRect = scrollContainer.getBoundingClientRect();
    }

    const { placementStyles, realPlacement: newRealPlacement } = calculatePlacementAndStyles({
      contentRect,
      triggerRect,
      scrollContainerRect,
      alignType,
      currentPlacement: realPlacement,
      shouldChangePlacementOnOverflowOnly,
      shouldLimitHeightByScreen,
      isVerticalAutoPlacementDisabled,
      scrollContainer,
    });

    setIsPlacementCalculated(true);
    setRealPlacement(isVirtualized ? 'bottom' : newRealPlacement);
    setPlacementStyles(placementStyles);
    setShouldChangePlacementOnOverflowOnly(true);
  };

  /*----------------------------hack----------------------------*/
  // Чтобы варианты не пропадали из оверлея во время транзишена выхода, был сделан такой хак
  // берем предыдущий рендер где были эти варианты и вставляем в настоящий

  const content = (
    <div
      style={{ maxHeight: (placementStyles as PlacementStyles).maxHeight }}
      ref={handleContentRef}
      className={cls(styles.content, styles.disableOutline, childrenClassNames.content, {
        [styles.isSmall]: isSmall,
        [styles.unlimitedHeight]: shouldLimitHeightByScreen,
        [styles.hasTopPlacement]: realPlacement === PLACEMENT.TOP,
      })}
      tabIndex={-1}
    >
      {renderOptions()}
    </div>
  );

  const previousContent = usePrevious(content);
  // тут берем content, т.к. в данном случае options еще не поменялся
  const onSelectOption = (value: SelectValue, option: Option) => {
    if (!isMultiple && !hasExternalControl) {
      setSnapshot(React.cloneElement(content));
    }

    onSelect(value, option);
  };

  useEffect(() => {
    if (!isOverlayOpen && previousContent && !snapshot) {
      // тут берем previousContent, т.к. в данном случае options уже поменялся
      setSnapshot(React.cloneElement(previousContent));
    }

    if (!isOverlayOpen) {
      setIsPlacementCalculated(false);
      setShouldChangePlacementOnOverflowOnly(false);
    }

    if (isOverlayOpen) {
      setSnapshot(null);
    }
  }, [isOverlayOpen]);
  /*--------------------------end hack--------------------------*/

  // тут контролируется управление кнопками, ховеры через стрелки и выбор через таб и энтер
  useKeyboardControl({
    setScrollTop: setVirtualizedScrollTop,
    hoveredOption,
    options,
    setHoveredOption,
    setShouldUseMouseHover,
    hasTopPlacement,
    contentRef,
    optionsRefs,
    isMultiple,
    onSelect: onSelectOption,
    isOverlayOpen,
    isVirtualized,
    virtualizationRowHeight,
  });

  // Нужно чтобы при изменении опшенов во время открытого оверлея он не менял расположение
  useEffect(() => {
    let hoverSet = false;

    optionsRefs.current = options.reduce<{ [key: string]: null | HTMLDivElement }>(
      (acc, option) => {
        if (!isOptionGroup(option)) {
          if (shouldSetInitialHover && !hoverSet && (!isOverlayOpen || !isMultiple)) {
            hoverSet = true;

            setHoveredOption(option);
          }

          acc[option.value] = optionsRefs.current[option.value] || null;
        }

        return acc;
      },
      {}
    );
  }, [options]);

  // пересчитываем стили оверлея когда это нужно
  useLayoutEffect(() => {
    const throttledApplyPlacementStyles = _throttle(applyPlacementStyles, 300);

    if (isOverlayOpen) {
      // пересчитываем стили при изменении опшенов (т.к. в автокомплите из-за опшенов может меняться высота оверлея)
      // или изменении стейта открыт/закрыт
      applyPlacementStyles();

      if (shouldLimitHeightByScreen) {
        window.addEventListener('scroll', applyPlacementStyles);
      }

      window.addEventListener('resize', throttledApplyPlacementStyles);
    }

    return () => {
      window.removeEventListener('scroll', applyPlacementStyles);
      window.removeEventListener('resize', throttledApplyPlacementStyles);
    };
  }, [
    contentRef.current,
    triggerRef.current,
    isOverlayOpen,
    options,
    shouldLimitHeightByScreen,
    shouldSetInitialHover,
  ]);

  // если прокинут проп, проставляем ховер на выбранный или первый попавшийся опшен,
  // после того как мы посчитали стили оверлея
  useLayoutEffect(() => {
    if (!shouldSetInitialHover || !isPlacementCalculated) {
      return;
    }

    if (isOverlayOpen && options.length > 0) {
      const { option: activeOrFirstOption, index: activeOptionIndex } = getActiveOrFirstOption(
        options,
        value
      );

      if (activeOrFirstOption) {
        setHoveredOption(activeOrFirstOption as Option);

        if (isVirtualized && contentRef.current) {
          const contentBoundingRect = contentRef.current.getBoundingClientRect();

          const totalHeight = options.length * virtualizationRowHeight;
          const newScrollTopWithContentHeight =
            activeOptionIndex * virtualizationRowHeight +
            contentBoundingRect.height -
            CONTENT_HEIGHT_PADDING;

          const diff =
            newScrollTopWithContentHeight - totalHeight > 0
              ? newScrollTopWithContentHeight - totalHeight
              : 0;

          setVirtualizedScrollTop(activeOptionIndex * virtualizationRowHeight - diff);
        } else if (
          contentRef.current &&
          optionsRefs.current &&
          optionsRefs.current[activeOrFirstOption.value]
        ) {
          const contentBoundingRect = contentRef.current.getBoundingClientRect();
          const optionBoundingRect =
            optionsRefs.current![activeOrFirstOption.value]!.getBoundingClientRect();
          const optionOffsetFromOverlay = optionBoundingRect.top - contentBoundingRect.top;

          if (optionOffsetFromOverlay + optionBoundingRect.height > contentBoundingRect.height) {
            contentRef.current.scrollTop +=
              optionOffsetFromOverlay + optionBoundingRect.height - optionBoundingRect.height;
          } else if (optionOffsetFromOverlay < 0) {
            contentRef.current.scrollTop += optionOffsetFromOverlay;
          }
        }
      }
    }
  }, [isPlacementCalculated]);

  const { document } = useWebApi();

  if (!document) {
    return null;
  }

  if (isInFullscreenMode) {
    return (
      <>
        <div className={cls(withShadow && styles.shadow)} />
        <div
          className={styles.fullscreenOverlayBody}
          ref={handleContentRef}
          onScroll={isVirtualized ? noop : checkFullscreenContainerScroll}
        >
          {renderOptions()}
        </div>
      </>
    );
  }

  return ReactDOM.createPortal(
    <CSSTransition
      classNames={getAnimationClassNames(realPlacement)}
      in={isOverlayOpen}
      timeout={ANIMATION_TIMEOUT}
      unmountOnExit={true}
      onExited={() => setPlacementStyles({} as PlacementStyles)}
    >
      <div
        style={placementStyles}
        className={cls(styles.contentWrapper, childrenClassNames.contentWrapper)}
        data-locator={`${specificLocator}-overlay-content`}
      >
        {snapshot ? snapshot : content}
      </div>
    </CSSTransition>,
    container || document.body
  );
}

export default forwardRef((props, ref?: ForwardedRef<HTMLDivElement | null>) => {
  return <Overlay {...props} forwardedRef={ref} />;
}) as <Option extends SelectOption = SelectOption>(
  props: OverlayProps<Option> & { ref?: ForwardedRef<HTMLDivElement | null> }
) => ReactElement;
